<template>
  <section class="overflow-hidden rounded-lg border border-slate-200 bg-white">
    <header class="border-b border-slate-200 px-4 py-3">
      <div class="flex flex-wrap items-center justify-between gap-3">
        <div class="min-w-0">
          <h3 class="text-base font-semibold text-slate-900">校验面板</h3>
          <p class="mt-1 text-sm text-slate-500">ERROR / WARNING / INFO</p>
        </div>
        <button
          type="button"
          class="btn-secondary inline-flex items-center gap-1.5"
          :disabled="busy"
          @click="emit('validate')"
        >
          重新校验
        </button>
      </div>

      <div class="mt-3 grid grid-cols-3 gap-2">
        <div
          v-for="item in counters"
          :key="item.key"
          :class="['rounded-md border px-3 py-2', item.className]"
        >
          <p class="text-xs font-medium">{{ item.label }}</p>
          <p class="mt-1 text-lg font-semibold leading-none">{{ item.count }}</p>
        </div>
      </div>
    </header>

    <div v-if="issues.length === 0" class="data-table-empty is-compact">
      暂无校验问题
    </div>

    <ul v-else class="divide-y divide-slate-100">
      <li v-for="(issue, index) in issues" :key="issue.id || `${issue.ruleCode || 'rule'}-${index}`"
          class="cursor-pointer px-4 py-3 transition-colors hover:bg-slate-50"
          @click="emit('locate', issue)">
        <div class="flex flex-wrap items-center gap-2">
          <span :class="['status-badge', severityMeta(severity(issue)).className]">{{ severity(issue) }}</span>
          <span class="truncate text-sm font-semibold text-slate-900">{{ issue.ruleCode || '-' }}</span>
        </div>
        <p class="mt-1 text-sm text-slate-700">{{ issue.message || '-' }}</p>
        <p v-if="relatedText(issue)" class="mt-1 text-xs text-slate-500">{{ relatedText(issue) }}</p>
      </li>
    </ul>

    <div v-if="hasError" class="border-t border-red-100 bg-red-50 px-4 py-2 text-xs font-medium text-red-700">
      存在 ERROR 问题，需处理后才能确认方案。
    </div>
  </section>
</template>

<script setup>
import { computed } from 'vue'

const props = defineProps({
  issues: { type: Array, default: () => [] },
  loading: { type: Boolean, default: false },
  syncing: { type: Boolean, default: false },
  hasError: { type: Boolean, default: false }
})

const emit = defineEmits(['validate', 'locate'])

const busy = computed(() => props.loading || props.syncing)

const counters = computed(() => [
  { key: 'ERROR', label: 'ERROR', count: countBySeverity('ERROR'), className: 'border-red-100 bg-red-50 text-red-700' },
  { key: 'WARNING', label: 'WARNING', count: countBySeverity('WARNING'), className: 'border-amber-100 bg-amber-50 text-amber-700' },
  { key: 'INFO', label: 'INFO', count: countBySeverity('INFO'), className: 'border-blue-100 bg-blue-50 text-blue-700' }
])

function severity(issue) {
  return String(issue?.severity || issue?.level || 'INFO').toUpperCase()
}

function countBySeverity(value) {
  return props.issues.filter(issue => severity(issue) === value).length
}

function severityMeta(value) {
  if (value === 'ERROR') return { className: 'bg-red-50 text-red-700' }
  if (value === 'WARNING') return { className: 'bg-amber-50 text-amber-700' }
  return { className: 'bg-blue-50 text-blue-700' }
}

function firstValue(...values) {
  return values.find(value => value !== null && value !== undefined && value !== '') ?? null
}

function relatedText(issue) {
  const chain = firstValue(issue?.chainNo, issue?.chainId)
  const customer = firstValue(issue?.customerName, issue?.companyNameSnapshot, issue?.companyName)
  if (chain && customer) return `链路 ${chain} · ${customer}`
  if (chain) return `链路 ${chain}`
  if (customer) return customer
  return ''
}
</script>
